<template>
  <div>
    <div v-if="selectedItems.length" class="table-action-menu-button-wrapper">
      <MenuButton :menu-items="menuItems" />
    </div>
    <DataTable
      v-if="news.length"
      v-model:selection="selectedItems"
      striped-rows
      paginator
      :rows="selectedPerPage"
      :value="news"
      :table-style="isMobile ? 'width: 100%' : 'min-width: 50rem'"
      selection-mode="single"
      @row-click="$emit('selectNews', $event.data.id)"
    >
      <template #header>
        <div class="d-flex align-items-center justify-content-end w-100">
          <TableHeader
            v-model="selectedPerPage"
            :total-pages="Math.ceil(news.length / selectedPerPage)"
            :target-length="news.length"
            :current-page="currentPage"
          />
        </div>
      </template>
      <Column
        v-if="false && !isMobile"
        selection-mode="multiple"
        header-style="width: 1.5rem"
      >
      </Column>
      <Column
        v-if="!isMobile"
        sortable
        field="forMembers"
        :header="isMobile ? '公開範囲' : '公開範囲'"
        :style="isMobile ? {} : { width: '120px', 'min-width': '120px' }"
      >
        <template #body="{ data }">
          <NewsLabelChip
            :label="
              !data.published ? '未公開' : data.forMembers ? '会員' : '一般'
            "
            reverse-color
            class="me-2"
          />
        </template>
      </Column>
      <Column
        v-if="!isMobile"
        sortable
        field="publishedAt"
        header="公開日時"
        :style="
          isMobile
            ? { width: '170px', 'min-width': '170px' }
            : { width: '170px', 'min-width': '170px' }
        "
      >
        <template #body="{ data }">
          <span>{{
            basicFormatter(data.publishedAt, "slashStyleWithZero")
          }}</span>
        </template>
      </Column>
      <Column
        sortable
        field="label"
        header="種別"
        :style="
          isMobile
            ? { width: '120px', 'min-width': '100px' }
            : { width: '120px', 'min-width': '100px' }
        "
      >
        <template #body="{ data }">
          <NewsLabelChip
            :label="labelTranslater(data.label)"
            reverse-color
            class="me-2"
          />
        </template>
      </Column>
      <Column
        sortable
        field="title"
        :header="isMobile ? 'ﾀｲﾄﾙ' : 'タイトル'"
        :style="isMobile ? {} : { width: '240px', 'min-width': '240px' }"
      >
        <template #body="{ data }">
          <span>{{ data.title }}</span>
        </template>
      </Column>

      <Column
        v-if="!isMobile"
        filed="content"
        header="本文"
        style="min-width: 150px"
      >
        <template #body="{ data }">
          <i v-if="data.attachedPdfs.length" class="pi pi-paperclip me-2"></i>
          <span>{{ stripHtml(data.content.slice(0, 50)) }}</span>
        </template>
      </Column>
      <Column
        v-if="!viewmode"
        filed="showTop"
        :header="isMobile ? 'TOP' : 'TOP掲載'"
        :style="
          isMobile
            ? {
                width: '40px',
                'max-width': '30px',
                'text-align': 'center',
              }
            : {
                width: '80px',
                'min-width': '70px',
                'text-align': 'center',
              }
        "
      >
        <template #body="{ data }">
          <div class="w-100 d-flex justify-content-center">
            <i v-if="data.showTop" class="pi pi-check"></i>
          </div>
        </template>
      </Column>
      <Column
        v-if="viewmode"
        filed="unread"
        :style="
          isMobile
            ? {
                width: '40px',
                'max-width': '30px',
                'text-align': 'center',
              }
            : {
                width: '80px',
                'min-width': '70px',
                'text-align': 'center',
              }
        "
      >
        <template #body="{ data }">
          <Tag v-if="data.unread" class="new-tag animate-blink" />
        </template>
      </Column>
      <Column
        v-if="!viewmode"
        :style="
          trashed
            ? { width: '90px', 'min-width': '90px' }
            : { width: '60px', 'min-width': '60px' }
        "
      >
        <template #body="{ data }">
          <div class="d-flex">
            <BasicButton
              v-if="trashed"
              variant="primary"
              icon="pi pi-refresh"
              need-icon
              text
              button-type="submit"
              class="me-2"
              @click="$emit('restoreNews', data.id)"
            />
            <BasicButton
              variant="danger"
              icon="pi pi-trash"
              need-icon
              text
              button-type="submit"
              @click="$emit('trashNews', data.id)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
    <span v-else>データがありません</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { basicFormatter } from "/@/modules/luxon";
import { stripHtml } from "/@/modules/string";
import { useMqUtils, useTableHeader } from "/@/vue/composables";
import { BasicButton, NewsLabelChip } from "/@/vue/components/Atom";
import { MenuButton } from "/@/vue/components/Molecules";
import { Header as TableHeader } from "/@/vue/components/Molecules/TableUtils";
import { NewsClient } from "/@/types";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Tag from "primevue/tag";
import type { MenuItem } from "primevue/menuitem";
import { labelTranslater } from "/@/modules/news";

const props = defineProps<{
  news: NewsClient[];
  trashed?: boolean;
  viewmode?: boolean;
}>();

const emit = defineEmits<{
  (e: "selectNews", id: number): void;
  (e: "trashNews", id: number | number[]): void;
  (e: "restoreNews", id: number | number[]): void;
}>();

const { isMobile } = useMqUtils();
const { selectedPerPage, currentPage, changePage } = useTableHeader();

const selectedItems = ref<NewsClient[]>([]);

const menuItems = computed<MenuItem[]>(() => {
  return [
    {
      label: "復元する",
      icon: "pi pi-refresh",
      visible: props.trashed,
      command: () => {
        emit(
          "restoreNews",
          selectedItems.value.map((n) => n.id)
        );
        selectedItems.value = [];
      },
    },
    {
      label: props.trashed ? "完全に削除する" : "ゴミ箱へ移動",
      icon: "pi pi-trash",
      command: () => {
        emit(
          "trashNews",
          selectedItems.value.map((n) => n.id)
        );
        selectedItems.value = [];
      },
    },
  ];
});
</script>

<style lang="scss">
// in Organizations/Tabel.vue
</style>
